import { Add, Remove } from '@mui/icons-material';
import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { uniqueByKey } from '../../../shared/utils/arrayUtils';
import { IngredientDosingDialog } from '../../IngredientsPage/components/IngredientDosingDialog';
import { StyledLink } from '../../../shared/StyledLink/StyledLink';
import { useTheme } from '@mui/material/styles';
import {
  DosingDto,
  IngredientCategoryDto,
  ProductIngredientDto,
  ProductSizeDto,
} from '@kotipizzagroup/kotipizza-products-api-client';
import { useAllDosings } from '../../../apis/products-api';
import { useTranslation } from 'react-i18next';
import { belongsInS4DRecipe, S4DProductComparison } from '../utils/s4dProductComparison';
import { getS4DIngredientText } from '../utils/s4dIssueMessages';
import { S4DRecipeStatusIcon } from './S4DRecipeStatusIcon';

export enum IngredientQuantityChangeType {
  ADD = 'ADD',
  SUBTRACT = 'SUBTRACT',
}

interface ProductRecipeGridProps {
  productCategoryId: number;
  productIngredientsRows: ProductIngredientDto[];
  comparison: S4DProductComparison | undefined;
  isRecipeLoaded: boolean;
  onIngredientQuantityChange: (ingredientId: number, changeType: IngredientQuantityChangeType) => void;
  onMaxQuantityChange: (ingredientId: number, value: string) => void;
  onIngredientRequiredChange: (ingredientId: number, value: boolean) => void;
}

type QuantityActionsProps = {
  ingredientId: number;
  onIngredientQuantityChange: (ingredientId: number, changeType: IngredientQuantityChangeType) => void;
};

const QuantityActions = (props: QuantityActionsProps) => {
  const { onIngredientQuantityChange, ingredientId } = props;
  return (
    <Box>
      <IconButton
        onClick={() => onIngredientQuantityChange(ingredientId, IngredientQuantityChangeType.SUBTRACT)}
        size="small"
        color="error"
      >
        <Remove />
      </IconButton>
      <IconButton
        onClick={() => onIngredientQuantityChange(ingredientId, IngredientQuantityChangeType.ADD)}
        size="small"
        color="success"
      >
        <Add />
      </IconButton>
    </Box>
  );
};

type IngredientDosingProps = {
  ingredientDosings: ProductIngredientDosingRow[];
  productSizeId: number;
  ingredientId: number;
  quantity: number;
};

const IngredientDosing = (props: IngredientDosingProps) => {
  const { ingredientDosings, ingredientId, productSizeId, quantity } = props;
  const { t } = useTranslation();

  const dosings = ingredientDosings.find((row) => row.ingredientId === ingredientId)?.dosings;

  const found = dosings?.find((dosing) => dosing.productSizeId === productSizeId);

  if (found === undefined) {
    return (
      <TableCell key={`no_dosing_${ingredientId}`}>
        <Typography fontWeight={700} color="error">
          {t('missing')}
        </Typography>
      </TableCell>
    );
  }

  const multiplier = quantity > 1 ? ` x ${quantity}` : '';

  return (
    <TableCell key={found.dosingId}>
      {/* eslint-disable-next-line i18next/no-literal-string */}
      {found.dosingInGrams}g{multiplier}
    </TableCell>
  );
};

interface ProductRecipeRow extends ProductIngredientDto {
  ingredientName: string | undefined | null;
  ingredientCategoryName: string | undefined | null;
  isActive: boolean | undefined | null;
}

type ProductIngredientDosingRow = {
  ingredientId: number;
  dosings: DosingDto[] | null | undefined;
};

export const ProductRecipeGrid = (props: ProductRecipeGridProps) => {
  const {
    productIngredientsRows,
    isRecipeLoaded,
    productCategoryId,
    comparison,
    onIngredientQuantityChange,
    onMaxQuantityChange,
    onIngredientRequiredChange,
  } = props;

  const [ingredientDetailsDialogOpen, setIngredientDetailsDialogOpen] = useState(false);
  const [ingredientDosingDialogId, setIngredientDosingDialogId] = useState(0);

  const { data: dosingsDetails, isLoading: isLoadingDosings, isFetching: isFetchingDosings } = useAllDosings();
  const { t } = useTranslation();

  const productIngredientsGridLoading = isLoadingDosings || isFetchingDosings;

  const productSizes = useMemo(
    () =>
      uniqueByKey<ProductSizeDto>(
        dosingsDetails?.productSizes?.filter((x: ProductSizeDto) => x.productCategoryId === productCategoryId) || [],
        'productSizeId'
      ),
    [dosingsDetails, productCategoryId]
  );

  const getIngredientName = useCallback(
    (ingredientId: number): string | null | undefined => {
      return dosingsDetails?.ingredients?.find((ingredient) => ingredient.ingredientId === ingredientId)?.name;
    },
    [dosingsDetails?.ingredients]
  );

  const getIngredientCategoryName = useCallback(
    (ingredientId: number): string | null | undefined => {
      const categoryId = dosingsDetails?.ingredients?.find(
        (ingredient) => ingredient.ingredientId === ingredientId
      )?.ingredientCategoryId;
      const category = dosingsDetails?.ingredientCategories?.find(
        (category: IngredientCategoryDto) => category.ingredientCategoryId === categoryId
      );
      return category?.name;
    },
    [dosingsDetails?.ingredientCategories, dosingsDetails?.ingredients]
  );

  const getIngredientActive = useCallback(
    (ingredientId: number): boolean | null | undefined => {
      return dosingsDetails?.ingredients?.find((x) => x.ingredientId === ingredientId)?.isActive;
    },
    [dosingsDetails?.ingredients]
  );

  const convertToRecipeRows = useCallback(
    (ingredients: ProductIngredientDto[]): ProductRecipeRow[] => {
      return ingredients.map((ing) => ({
        ...ing,
        // TODO: ProductIngredientDto ingredientId shouldn't be nullable
        ingredientName: getIngredientName(ing.ingredientId || 0),
        ingredientCategoryName: getIngredientCategoryName(ing.ingredientId || 0),
        isActive: getIngredientActive(ing.ingredientId || 0),
      }));
    },
    [getIngredientActive, getIngredientCategoryName, getIngredientName]
  );

  const gridRows = useMemo(
    () => convertToRecipeRows(productIngredientsRows),
    [convertToRecipeRows, productIngredientsRows]
  );

  const ingredientDosings: ProductIngredientDosingRow[] = useMemo(
    () =>
      gridRows.map((row: ProductRecipeRow) => {
        const dosings: DosingDto[] = [];

        productSizes.forEach((ps) => {
          const dosing = dosingsDetails?.ingredientDosings?.find(
            (x) => x.ingredientId === row.ingredientId && x.productSizeId === ps.productSizeId
          );

          if (dosing) {
            dosings.push({ ...dosing });
          }
        });

        return {
          ingredientId: row.ingredientId,
          isActive: dosingsDetails?.ingredients?.find((x) => x.ingredientId === row.ingredientId)?.isActive,
          dosings: dosings,
        };
      }),
    [dosingsDetails?.ingredientDosings, dosingsDetails?.ingredients, gridRows, productSizes]
  );

  const handleIngredientNameClick = (id: number) => {
    setIngredientDosingDialogId(id);
    setIngredientDetailsDialogOpen(true);
  };

  // Every S4D copy of an extra topping, since each of them has to be removed.
  const missingFromRecipe = (comparison?.extraS4DToppings || [])
    .flatMap((extra) =>
      extra.s4dToppings.map((topping) => ({
        topping,
        s4dProductId: extra.s4dProductId,
        s4dProductName: extra.s4dProductName,
      }))
    )
    .filter(
      (extra, index, all) =>
        all.findIndex(
          (other) => other.topping.s4dId === extra.topping.s4dId && other.s4dProductName === extra.s4dProductName
        ) === index
    );

  const isComparedInS4D = useCallback(
    (ingredientId: number): boolean => {
      if (!comparison?.isRecipeCompared) return false;

      const categoryId = dosingsDetails?.ingredients?.find(
        (ingredient) => ingredient.ingredientId === ingredientId
      )?.ingredientCategoryId;

      return belongsInS4DRecipe(categoryId, productCategoryId);
    },
    [comparison?.isRecipeCompared, dosingsDetails?.ingredients, productCategoryId]
  );

  const theme = useTheme();

  return (
    <Box paddingTop="1em" paddingBottom="1em">
      {!!ingredientDosingDialogId && (
        <IngredientDosingDialog
          title="Ingredient dosings"
          ingredientId={ingredientDosingDialogId as unknown as number}
          open={ingredientDetailsDialogOpen}
          onDialogClose={() => setIngredientDetailsDialogOpen(false)}
        />
      )}
      {isRecipeLoaded && !productIngredientsGridLoading && missingFromRecipe.length > 0 && (
        <Box>
          <Typography color="error" variant="h6" component="div">
            <b>{t('S4D recipe includes additional toppings')}</b>
            <Typography color="error" variant="body2" component="div">
              <ul>
                {missingFromRecipe.map((extra) => (
                  <li key={`${extra.s4dProductId}-${extra.topping.s4dId}`}>
                    {getS4DIngredientText({
                      ingredientId: extra.topping.ingredientId || undefined,
                      name: extra.topping.name,
                      note: extra.topping.isDuplicate ? t('duplicate') : undefined,
                    })}
                    {extra.s4dProductName ? ` (${extra.s4dProductName})` : ''}
                  </li>
                ))}
              </ul>
            </Typography>
          </Typography>
        </Box>
      )}
      <TableContainer component={Paper}>
        <LinearProgress sx={{ visibility: productIngredientsGridLoading ? 'visible' : 'hidden' }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>{t('Ingredient')}</TableCell>
              <TableCell>
                {t('global.delete')}/{t('global.add').toLowerCase()}
              </TableCell>
              {productSizes.map((size) => (
                <TableCell key={size.productSizeId}>
                  {t('Size')}: {size.name}
                </TableCell>
              ))}
              <TableCell>{t('S4D recipe status')}</TableCell>
              <TableCell>{t('Customer can remove')}</TableCell>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <TableCell>Max</TableCell>
              <TableCell>{t('Ingredient id')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gridRows.map((row) => {
              return (
                <TableRow
                  key={row.ingredientId}
                  sx={{ background: row.isActive ? 'inherit' : theme.palette.error.light }}
                >
                  <TableCell>
                    <Typography variant="body2" color="primary" fontWeight={700}>
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      {row.quantity} x{' '}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <StyledLink onClick={() => handleIngredientNameClick(row.ingredientId || 0)}>
                        {row.ingredientName}
                      </StyledLink>
                      <Typography variant="body2">{row.ingredientCategoryName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <QuantityActions
                      ingredientId={row.ingredientId || 0}
                      onIngredientQuantityChange={onIngredientQuantityChange}
                    />
                  </TableCell>
                  {productSizes.map((size) => (
                    <IngredientDosing
                      key={size.productSizeId}
                      ingredientDosings={ingredientDosings}
                      productSizeId={size.productSizeId}
                      ingredientId={row.ingredientId}
                      quantity={row.quantity}
                    />
                  ))}
                  <TableCell>
                    <S4DRecipeStatusIcon
                      comparison={comparison}
                      ingredientId={row.ingredientId}
                      isCompared={isComparedInS4D(row.ingredientId)}
                    />
                  </TableCell>
                  <TableCell width="150px">
                    <Switch
                      size="small"
                      onChange={(event) => onIngredientRequiredChange(row.ingredientId || 0, !event.target.checked)}
                      checked={row.minQuantity === 0}
                      value={row.minQuantity === 1 ? 1 : 0}
                    />
                  </TableCell>
                  <TableCell width="75px">
                    <TextField
                      onChange={(event) => onMaxQuantityChange(row.ingredientId || 0, event.currentTarget.value)}
                      value={row.maxQuantity}
                    />
                  </TableCell>
                  <TableCell width="120px">{row.ingredientId}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
